import {Icon} from "leaflet";
import "leaflet/dist/leaflet.css";
import {MapPin} from "lucide-react";
import {useCallback, useLayoutEffect, useRef, useState} from "react";
import {MapContainer, Marker, TileLayer} from "react-leaflet";

import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {cn} from "@/lib/utils";

const legalIcon = new Icon({
  iconUrl: `/assets/location.png`,
  iconSize: [35, 35],
});

/**
 * @param {string | string[] | null | undefined} position - Raw "lat,lng", or split parts
 * @returns {[number, number] | null}
 */
function parseLatLng(position) {
  if (position == null) return null;
  const raw = Array.isArray(position) ? position.join(",") : String(position);
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(",");
  if (parts.length < 2) return null;
  const lat = parseFloat(parts[0].trim());
  const lng = parseFloat(parts[1].trim());
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

/**
 * When `includeGpsCoordinates` is passed (e.g. DAR API), map is disabled unless it is `1`.
 * When omitted, only coordinate validity is used (legacy callers).
 *
 * @param {number | string | null | undefined} includeGpsCoordinates
 */
function isGpsOptedIn(includeGpsCoordinates) {
  if (includeGpsCoordinates === undefined || includeGpsCoordinates === null || includeGpsCoordinates === "") {
    return true;
  }
  return Number(includeGpsCoordinates) === 1;
}

/**
 * @param {number | string | null | undefined} includeGpsCoordinates
 */
function getDisabledMapLabel(includeGpsCoordinates) {
  if (!isGpsOptedIn(includeGpsCoordinates)) {
    return "GPS coordinates not included";
  }
  return "No GPS coordinates";
}

/**
 * @param {HTMLElement | null} triggerEl
 * @returns {{ width: number; alignOffset: number; collisionBoundary: HTMLElement | null }}
 */
function measureCardAnchoredPopover(triggerEl) {
  if (!triggerEl || typeof window === "undefined") {
    return {
      width: 512,
      alignOffset: 0,
      collisionBoundary: null,
    };
  }

  const card = triggerEl.closest("[data-slot=\"card\"]");
  if (card) {
    const cw = card.getBoundingClientRect();
    const tw = triggerEl.getBoundingClientRect();
    return {
      width: Math.round(cw.width),
      // align="start" pins popover start to trigger start; shift left so popover lines up with card start
      alignOffset: Math.round(cw.left - tw.left),
      collisionBoundary: card,
    };
  }

  const w = window.innerWidth;
  return {
    width: Math.min(512, Math.max(280, w - 32)),
    alignOffset: 0,
    collisionBoundary: null,
  };
}

function MapPopoverInteractive({position, text, triggerClassName}) {
  const latLng = parseLatLng(position);
  const [lat, lng] = latLng ?? [0, 0];

  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [metrics, setMetrics] = useState(() => ({
    width: 512,
    alignOffset: 0,
    collisionBoundary: null,
  }));

  const refreshMetrics = useCallback(() => {
    setMetrics(measureCardAnchoredPopover(triggerRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    refreshMetrics();
    const card = triggerRef.current?.closest("[data-slot=\"card\"]");
    if (!card) {
      window.addEventListener("resize", refreshMetrics);
      return () => window.removeEventListener("resize", refreshMetrics);
    }
    const ro = new ResizeObserver(() => refreshMetrics());
    ro.observe(card);
    window.addEventListener("resize", refreshMetrics);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", refreshMetrics);
    };
  }, [open, refreshMetrics]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        ref={triggerRef}
        className={cn(
          "flex cursor-pointer items-center gap-1",
          triggerClassName ?? "text-primary",
        )}
        aria-label={text ? undefined : "View location on map"}
      >
        {text}
        <MapPin className="size-4 shrink-0" />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        alignOffset={metrics.alignOffset}
        collisionBoundary={metrics.collisionBoundary ?? undefined}
        className="box-border max-w-none p-2.5"
        style={{
          width: metrics.width,
          maxWidth: metrics.width,
        }}
      >
        <div className="relative z-0 w-full overflow-hidden rounded-md border border-border">
          <MapContainer
            className="h-[18rem] w-full md:h-[25rem]"
            center={[lat, lng]}
            zoom={15}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[lat, lng]} icon={legalIcon}></Marker>
          </MapContainer>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * @param {Object} props
 * @param {string | string[] | null | undefined} props.position
 * @param {string} [props.text]
 * @param {string} [props.triggerClassName]
 * @param {number | string | null | undefined} [props.includeGpsCoordinates]
 * @param {boolean} [props.forceInteractive]
 */
export default function MapPopover({
  position,
  text = "Locate in map",
  triggerClassName = undefined,
  includeGpsCoordinates = undefined,
  /** Admin / senior supervisor: ignore API opt-out when coordinates exist */
  forceInteractive = false,
}) {
  const latLng = parseLatLng(position);
  const interactive =
    Boolean(latLng) &&
    (forceInteractive || isGpsOptedIn(includeGpsCoordinates));

  if (!interactive) {
    const label = getDisabledMapLabel(includeGpsCoordinates);
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1",
          triggerClassName,
          "cursor-default text-destructive [&_svg]:text-destructive",
        )}
        aria-label={label}
        title={label}
      >
        {text}
        <MapPin className="size-4 shrink-0" />
      </span>
    );
  }

  return <MapPopoverInteractive position={position} text={text} triggerClassName={triggerClassName} />;
}
